import { useEffect, useRef, useState } from "react"
import { animated, useTransition } from "@react-spring/web"
import { motion } from "framer-motion"
import { io } from "socket.io-client"
import { useGesture } from "react-use-gesture"
import styled from "styled-components"
import * as THREE from "three"
import * as POSTPROCESSING from "postprocessing"
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader"
import { PMREMGenerator } from "three/src/extras/PMREMGenerator"
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls"
import { CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer"
import Loader from "../Loader"
import Composer from "./Gallery3D.Composer"
import {
  lerp,
  SPACE_SIZE,
  TILE_MOVEMENT_DIRECTION,
  isEventSupported,
  TILE_CREATE_INTERVAL,
  SCREEN_SAVER_TIME
} from "./Gallery3D.Constants"
import Tile from "./Gallery3D.Tile"
import normalizeWheel from "normalize-wheel"
import DragTooltipDesktop from "../Tooltips/DragTooltipDesktop"
import "animate.css"
import "./Gallery3D.css"
import { IconButton } from "@material-ui/core"
import Arrows from "../Icons/navy_double_arrow_left.svg"
import { LazyLoadImage } from "react-lazy-load-image-component"

const TitleVariants = {
  initial: {
    opacity: 0,
    y: 50
  },
  in: {
    opacity: 1,
    y: 0
  },
  out: {
    opacity: 0,
    y: 50
  }
}

const TitleTransition = {
  ease: "easeOut",
  duration: 0.8
}

const url = process.env.REACT_APP_MARKETING_SUITE
const role = process.env.REACT_APP_ROLE

const TooltipPivot = styled.div`
  position: absolute;
  top: 85%;
  left: 50%;
  z-index: 1;
  transform: translate(-50%, -50%);
`

const DialogHolder = styled(animated.div)`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 9999999;
  width: 100%;
  height: 100%;
  background-color: white;
`

const DialogContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Dialog = ({ open = true, children }) => {
  const transition = useTransition(open, {
    from: { opacity: 0, transform: "translate3d(0,-100%,0)" },
    enter: { opacity: 1, transform: "translate3d(0,0,0)", delay: 500 },
    leave: { opacity: 0, transform: "translate3d(0,-100%,0)" }
  })

  return transition(
    (props, item) =>
      item && (
        <DialogHolder style={{ ...props }}>
          <DialogContent>{children}</DialogContent>
        </DialogHolder>
      )
  )
}

let speed = 0
let screenSaverTimer = 0
let isStarted = false

export default function Gallery3D({ title, tiles, length }) {
  const tileData = tiles || []
  const galleryLength = length
  const canvasContainer = useRef(null)
  const [showLoader, setShowLoader] = useState(true)
  const [titleAnimate, setTitleAnimate] = useState("in")
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedTileData, setSelectedTileData] = useState(null)

  const socket = useRef()

  const [tooltipVisible, setTooltipVisible] = useState(true)

  const handleDrag = state => {
    //Hide tooltip
    if (tooltipVisible) {
      setTooltipVisible(false)
    }
  }

  const handleWheel = state => {
    //Hide tooltip
    if (tooltipVisible) {
      setTooltipVisible(false)
    }
  }

  const bind = useGesture({
    onDrag: state => handleDrag(state),
    onWheel: state => handleWheel(state)
  })

  useEffect(() => {
    // Handle socket connection
    socket.current = io(url)
    if (role === "recipient") {
      socket.current.on("gallery", data => {
        if (data) {
          setTooltipVisible(data.tooltipVisible)
          setOpenDialog(data.openDialog)
          setSelectedTileData(data.selectedTileData)
          setTitleAnimate(data.titleAnimate)
        }
      })
      socket.current.on("gallery-scroll", data => {
        scroll.target = data
      })
    }

    let width = canvasContainer.current.offsetWidth
    let height = canvasContainer.current.offsetHeight

    let smaaSearchImage = null
    let smaaAreaImage = null

    const clock = new THREE.Clock()
    let composer = null

    const rayCaster = new THREE.Raycaster()
    const intersects = []

    const tiles = []
    let offeredTile = null
    let selectedTile = null

    let scroll = {
      ease: 0.05,
      current: 0,
      target: 0,
      last: 0
    }

    let direction = TILE_MOVEMENT_DIRECTION.up

    let isPointerDown = false
    let start = 0
    let viewport = {
      width: 5,
      height: 3
    }
    let screen = {
      width: width,
      height: height
    }

    let tileIntervalID

    /**
     * Scene
     */
    const SCENE_BACKGROUND_COLOR = 0xe5e5e5
    const scene = new THREE.Scene()
    scene.background = new THREE.Color(SCENE_BACKGROUND_COLOR)

    //Root object
    const rootObject = new THREE.Object3D()
    scene.add(rootObject)

    /**
     * Lighter
     */
    const lightGroup = new THREE.Group()
    lightGroup.name = "LightGroup"

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.1)
    dirLight.position.set(SPACE_SIZE * 0.5, SPACE_SIZE * 0.5, SPACE_SIZE)
    dirLight.target.position.set(0, 0, 0)
    dirLight.castShadow = true
    dirLight.shadow.mapSize.width = 2048
    dirLight.shadow.mapSize.height = 2048
    dirLight.shadow.camera.near = 0.01
    dirLight.shadow.camera.far = SPACE_SIZE * 2
    lightGroup.add(dirLight)

    scene.add(lightGroup)

    /**
     * Helper
     */
    // const axisHelper = new THREE.AxesHelper(5)
    // scene.add(axisHelper)

    // const shadowPlane = ShadowPlane()
    // scene.add(shadowPlane)

    /**
     * Camera
     */
    const CAMERA_Z = SPACE_SIZE * 0.3
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.01, SPACE_SIZE * 100)
    camera.position.set(0, 0, CAMERA_Z)
    camera.lookAt(0, 0, 0)

    function calcFov() {
      if (screen.width <= 600) {
        camera.fov = 100
      } else if (screen.width > 600 && screen.width <= 1024) {
        camera.fov = 80
      } else if (screen.width > 1024 && screen.width <= 1400) {
        camera.fov = 60
      } else {
        camera.fov = 50
      }
      camera.updateProjectionMatrix()
    }
    calcFov()

    /**
     * Resize & Render
     */
    function resizeRendererToDisplaySize() {
      const canvasWidth = renderer.domElement.offsetWidth
      const canvasHeight = renderer.domElement.offsetHeight
      const needResize = canvasWidth !== width || canvasHeight !== height
      if (needResize) {
        width = canvasWidth
        height = canvasHeight
        screen.width = width
        screen.height = height
        camera.aspect = width / height

        calcFov()

        camera.updateProjectionMatrix()
        renderer.setSize(width, height)
        renderer2D.setSize(width, height)
        composer.setSize(width, height)
      }
    }

    function render() {
      resizeRendererToDisplaySize()
      cameraController.update()
      renderer.render(scene, camera)
      renderer2D.render(scene, camera)
      if (composer) {
        composer.render(clock.getDelta())
      }
      animateTiles()
      requestAnimationFrame(render)
    }

    /**
     * Renderer
     */
    const renderer = new THREE.WebGLRenderer({
      powerPreference: "high-performance",
      antialias: false,
      stencil: false,
      depth: false
    })
    renderer.setSize(width, height, false)
    renderer.setPixelRatio(window.devicePixelRatio || 1)
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = THREE.PCFSoftShadowMap

    canvasContainer.current.appendChild(renderer.domElement)

    //2D renderer
    const renderer2D = new CSS2DRenderer()
    renderer2D.setSize(width, height)
    renderer2D.domElement.className = "renderer2D"
    canvasContainer.current.appendChild(renderer2D.domElement)

    /**
     * Camera Controller
     */
    const cameraController = new OrbitControls(camera, renderer.domElement)
    cameraController.screenSpacePanning = true
    cameraController.enableRotate = false
    cameraController.enablePan = false
    cameraController.enableZoom = false
    cameraController.dampingFactor = 0.05
    cameraController.enableDamping = false

    /**
     * Load Assets
     */
    const loadingManager = new THREE.LoadingManager()
    loadingManager.onStart = (url, itemsLoaded, itemsTotal) => {
      setShowLoader(true)
    }
    loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
      if (!showLoader) {
        setShowLoader(true)
      }
    }
    loadingManager.onLoad = () => {
      createTiles()
      render()
      setTimeout(() => {
        setShowLoader(false)
        isStarted = true
      }, 200)
    }

    const textureLoader = new THREE.TextureLoader(loadingManager)
    tileData.map(d => (d.texture = textureLoader.load(d.thumbnail_path)))

    //Load smaa images
    const smaaImageLoader = new POSTPROCESSING.SMAAImageLoader(loadingManager)
    smaaImageLoader.load(([search, area]) => {
      smaaSearchImage = search
      smaaAreaImage = area
      composer = Composer(renderer, scene, camera, smaaSearchImage, smaaAreaImage)
    })

    //Load env map
    const rgbeLoader = new RGBELoader(loadingManager)
    rgbeLoader.setDataType(THREE.UnsignedByteType).load("/assets/envmaps/city1.hdr", texture => {
      const pg = new PMREMGenerator(renderer)
      pg.compileEquirectangularShader()
      const envMap = pg.fromEquirectangular(texture).texture
      scene.environment = envMap
      texture.dispose()
      pg.dispose()
    })

    /**
     * Create tiles
     */
    function createTile() {
      if (tiles.length < tileData.length) {
        const d = tileData[tiles.length]
        const tile = new Tile(d)
        rootObject.add(tile)
        tiles.push(tile)
        intersects.push(tile.plane)
      } else {
        clearInterval(tileIntervalID)
      }
    }

    function createTiles() {
      tileIntervalID = setInterval(createTile, TILE_CREATE_INTERVAL)
    }

    function animateTiles() {
      scroll.target += speed
      scroll.current = lerp(scroll.current, scroll.target, scroll.ease)
      if (scroll.current > scroll.last) {
        direction = TILE_MOVEMENT_DIRECTION.down
      } else if (scroll.current < scroll.last) {
        direction = TILE_MOVEMENT_DIRECTION.up
        speed = -speed
      }
      tiles.map(tile => tile.animate(scroll.current / 800, direction, viewport, galleryLength))

      scroll.last = scroll.current
    }

    renderer.render(scene, camera)

    //Screen Saver
    setInterval(() => {
      if (isStarted) {
        screenSaverTimer += 200
        if (screenSaverTimer > 60000 * SCREEN_SAVER_TIME) {
          speed = 1
        }
      }
    }, 200)

    function endScreenSaver() {
      screenSaverTimer = 0
      speed = 0
    }

    /**
     * Event & Dispose
     */
    function onWheel(event) {
      endScreenSaver()
      const normalized = normalizeWheel(event)
      scroll.target += normalized.pixelY * 0.5
      if (role === "sender") {
        socket.current.emit("gallery-scroll", scroll.target)
      }

      if (titleAnimate === "in") setTitleAnimate("out")
    }
    function onPointerDown(event) {
      endScreenSaver()
      isPointerDown = true
      scroll.position = scroll.current
      start = event.touches ? event.touches[0].clientX : event.clientX

      //Pick a tile up
      {
        const pickedPoint = new THREE.Vector2(
          ((event.touches ? event.changedTouches[0].pageX : event.offsetX) / width) * 2 - 1,
          -((event.touches ? event.changedTouches[0].pageY : event.offsetY) / height) * 2 + 1
        )
        rayCaster.setFromCamera(pickedPoint, camera)
        let pickedObjs = rayCaster.intersectObjects(intersects)
        if (pickedObjs.length > 0) {
          offeredTile = pickedObjs[0].object.parent
        } else {
          offeredTile = null
        }
      }
    }
    function onPointerUp(event) {
      endScreenSaver()
      isPointerDown = false

      //Pick a tile up
      // eslint-disable-next-line
      {
        const pickedPoint = new THREE.Vector2(
          ((event.touches ? event.changedTouches[0].pageX : event.offsetX) / width) * 2 - 1,
          -((event.touches ? event.changedTouches[0].pageY : event.offsetY) / height) * 2 + 1
        )
        rayCaster.setFromCamera(pickedPoint, camera)
        let pickedObjs = rayCaster.intersectObjects(intersects)
        if (pickedObjs.length > 0) {
          if (offeredTile === pickedObjs[0].object.parent) {
            selectedTile = offeredTile
          } else {
            selectedTile = null
          }
        } else {
          selectedTile = null
        }
      }

      //Animation for a picked tile
      // eslint-disable-next-line
      {
        if (selectedTile) {
          setSelectedTileData(selectedTile.data)
          setOpenDialog(true)
          selectedTile = null
        }
      }
    }
    function onPointerMove(event) {
      endScreenSaver()
      if (selectedTile) return

      //Pick a tile up
      {
        const pickedPoint = new THREE.Vector2(
          ((event.touches ? event.changedTouches[0].pageX : event.offsetX) / width) * 2 - 1,
          -((event.touches ? event.changedTouches[0].pageY : event.offsetY) / height) * 2 + 1
        )
        rayCaster.setFromCamera(pickedPoint, camera)
        let pickedObjs = rayCaster.intersectObjects(intersects)
        if (pickedObjs.length > 0) {
          document.body.style.cursor = "pointer"
        } else {
          document.body.style.cursor = "default"
        }
      }

      if (isPointerDown) {
        const x = event.touches ? event.touches[0].clientX : event.clientX
        const distance = (start - x) * 2
        scroll.target = scroll.position + distance
        if (role === "sender") {
          socket.current.emit("gallery-scroll", scroll.target)
        }

        if (titleAnimate === "in") setTitleAnimate("out")
      }

      //
      offeredTile = null
    }
    // Add event listeners
    canvasContainer.current.addEventListener("mousewheel", onWheel)
    canvasContainer.current.addEventListener("wheel", onWheel)
    if (isEventSupported("onpointerdown")) {
      canvasContainer.current.addEventListener("pointerdown", onPointerDown, false)
      canvasContainer.current.addEventListener("pointermove", onPointerMove, false)
      canvasContainer.current.addEventListener("pointerup", onPointerUp, false)
      canvasContainer.current.addEventListener("pointercancel", onPointerUp, false)
      canvasContainer.current.addEventListener("pointerout", onPointerUp, false)
    } else if (isEventSupported("ontouchstart")) {
      canvasContainer.current.addEventListener("touchstart", onPointerDown, false)
      canvasContainer.current.addEventListener("touchmove", onPointerMove, false)
      canvasContainer.current.addEventListener("touchend", onPointerUp, false)
      canvasContainer.current.addEventListener("touchcancel", onPointerUp, false)
    } else if (isEventSupported("onmousedown")) {
      canvasContainer.current.addEventListener("mousedown", onPointerDown, false)
      canvasContainer.current.addEventListener("mousemove", onPointerMove, false)
      canvasContainer.current.addEventListener("mouseup", onPointerUp, false)
      canvasContainer.current.addEventListener("mouseleave", onPointerUp, false)
      canvasContainer.current.addEventListener("mouseout", onPointerUp, false)
    }

    return () => {
      // eslint-disable-next-line
      if (canvasContainer.current) {
        canvasContainer.current.removeEventListener("mousewheel", onWheel)
        canvasContainer.current.removeEventListener("wheel", onWheel)
        if (isEventSupported("onpointerdown")) {
          canvasContainer.current.removeEventListener("pointerdown", onPointerDown, false)
          canvasContainer.current.removeEventListener("pointermove", onPointerMove, false)
          canvasContainer.current.removeEventListener("pointerup", onPointerUp, false)
          canvasContainer.current.removeEventListener("pointercancel", onPointerUp, false)
          canvasContainer.current.removeEventListener("pointerout", onPointerUp, false)
        } else if (isEventSupported("ontouchstart")) {
          canvasContainer.current.removeEventListener("touchstart", onPointerDown, false)
          canvasContainer.current.removeEventListener("touchmove", onPointerMove, false)
          canvasContainer.current.removeEventListener("touchend", onPointerUp, false)
          canvasContainer.current.removeEventListener("touchcancel", onPointerUp, false)
        } else if (isEventSupported("onmousedown")) {
          // eslint-disable-next-line
          canvasContainer.current.removeEventListener("mousedown", onPointerDown, false)
          // eslint-disable-next-line
          canvasContainer.current.removeEventListener("mousemove", onPointerMove, false)
          // eslint-disable-next-line
          canvasContainer.current.removeEventListener("mouseup", onPointerUp, false)
          // eslint-disable-next-line
          canvasContainer.current.removeEventListener("mouseleave", onPointerUp, false)
          // eslint-disable-next-line
          canvasContainer.current.removeEventListener("mouseout", onPointerUp, false)
        }
        // eslint-disable-next-line
        canvasContainer.current.innerHTML = ""
      }
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (role === "sender") {
      socket.current.emit("gallery", {
        openDialog: openDialog,
        selectedTileData: selectedTileData,
        tooltipVisible: tooltipVisible,
        titleAnimate: titleAnimate
      })
    }
  }, [openDialog, tooltipVisible, titleAnimate])

  return (
    <>
      <div className="gallery3d" {...bind()}>
        {showLoader && <Loader />}
        <Dialog open={openDialog}>
          <BackButton
            onClick={() => {
              setOpenDialog(false)
              setTimeout(() => {
                setSelectedTileData(null)
              }, 500)
            }}
          >
            <BackIcon src={Arrows} />
          </BackButton>
          {selectedTileData && (
            <LazyLoadImage alt="" height="100%" src={selectedTileData.path} width="auto" />
          )}
        </Dialog>
        <TooltipPivot>
          {role !== "recipient" && (
            <DragTooltipDesktop isVisible={tooltipVisible}></DragTooltipDesktop>
          )}
        </TooltipPivot>
        <div
          className="title-wraper"
          style={{
            top: title.top,
            left: title.left,
            right: title.right,
            bottom: title.bottom,
            width: title.width
          }}
        >
          <motion.div
            initial="initial"
            animate={titleAnimate}
            variants={TitleVariants}
            transition={TitleTransition}
          >
            <img src={title.icon} alt=""></img>
          </motion.div>
        </div>
        <div className="canvasContainer" ref={canvasContainer}></div>
      </div>
    </>
  )
}

const BackButton = styled(IconButton)`
  position: absolute !important;
  bottom: 2vw;
  left: 2vw;
  z-index: 1;
  width: 3em;
`
const BackIcon = styled.img`
  width: 100%;
`
